/**
 * 堡垒锻造的 LocalStorage 存储存档系统。
 *
 * 纯模块——无 GameEngine 依赖。将 GameState + SessionStats
 * 序列化到命名存储槽中，供 UI 进行存档/读档/删除操作。
 *
 * 使用可注入的 `StorageAdapter` 和 `TimeSource`，使 GameCore 保持
 * 无浏览器 API 依赖（CLAUDE.md §5）。
 *
 * 键布局：
 *   bastion-save-{mapId}   → SaveSlot JSON
 *   bastion-stats          → SessionStats JSON
 */

import type { GameState } from "./types";
import type { StorageAdapter, TimeSource } from "./repos";
import { BrowserStorageAdapter, SystemTimeSource } from "./repos";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** 存档创建时的 Unix 时间戳（毫秒）。 */
export interface SaveSlot {
  schemaVersion: number;
  mapId: string;
  gameState: GameState;
  sessionStats: SessionStats;
  savedAt: number;
}

/** 跨会话的聚合统计数据。 */
export interface SessionStats {
  gamesPlayed: number;
  totalWins: number;
  totalLosses: number;
  /** 所有已完成游戏中累计获得的金币（加载存档前） */
  totalGoldEarned: number;
  /** 所有已完成游戏中累计击杀的敌人数 */
  totalEnemiesKilled: number;
  /** 所有已完成游戏的平均达成波次（若无则为 0） */
  avgWaveReached: number;
}

// ---------------------------------------------------------------------------
// 内部辅助函数
// ---------------------------------------------------------------------------

const SAVE_SCHEMA_VERSION = 2;

const SAVE_KEY_PREFIX = "bastion-save-";
const STATS_KEY = "bastion-stats";

function saveKey(mapId: string): string {
  return `${SAVE_KEY_PREFIX}${mapId}`;
}

function readJson<T>(key: string, storage: StorageAdapter): T | null {
  try {
    const raw = storage.getItem(key);
    if (raw === null) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJson<T>(key: string, value: T, storage: StorageAdapter): void {
  storage.setItem(key, JSON.stringify(value));
}

// ---------------------------------------------------------------------------
// 公共 API
// ---------------------------------------------------------------------------

/**
 * 将当前游戏状态和会话统计数据持久化到 `mapId` 下。
 * 覆盖同一地图的已有存档。
 */
export function saveGame(
  mapId: string,
  gameState: GameState,
  sessionStats: SessionStats,
  storage: StorageAdapter = BrowserStorageAdapter,
  timeSource: TimeSource = SystemTimeSource,
): void {
  const slot: SaveSlot = {
    schemaVersion: SAVE_SCHEMA_VERSION,
    mapId,
    gameState,
    sessionStats,
    savedAt: timeSource.now(),
  };
  writeJson(saveKey(mapId), slot, storage);
}

/**
 * 恢复之前为 `mapId` 保存的游戏。
 * 若该地图不存在存档则返回 null。
 */
export function loadGame(
  mapId: string,
  storage: StorageAdapter = BrowserStorageAdapter,
): SaveSlot | null {
  return readJson<SaveSlot>(saveKey(mapId), storage);
}

/**
 * 移除单个存档槽。若找到并删除了存档则返回 true。
 */
export function deleteSave(
  mapId: string,
  storage: StorageAdapter = BrowserStorageAdapter,
): boolean {
  const key = saveKey(mapId);
  if (storage.getItem(key) === null) return false;
  storage.removeItem(key);
  return true;
}

/**
 * 按 savedAt 降序（最新优先）列出所有存档槽。
 * 每项仅包含存档列表 UI 所需的元数据——不含完整的可变 GameState，
 * 以避免意外修改已存储的状态。
 */
export function listSaves(storage: StorageAdapter = BrowserStorageAdapter): Array<{
  mapId: string;
  savedAt: number;
  wave: number;
  gold: number;
  lives: number;
}> {
  const results: Array<{
    mapId: string;
    savedAt: number;
    wave: number;
    gold: number;
    lives: number;
  }> = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (!key || !key.startsWith(SAVE_KEY_PREFIX)) continue;
    const slot = readJson<SaveSlot>(key, storage);
    if (!slot) continue;
    const mapId = key.slice(SAVE_KEY_PREFIX.length);
    results.push({
      mapId,
      savedAt: slot.savedAt,
      wave: slot.gameState.wave,
      gold: slot.gameState.gold,
      lives: slot.gameState.baseHp,
    });
  }
  results.sort((a, b) => b.savedAt - a.savedAt);
  return results;
}

// ---------------------------------------------------------------------------
// 会话级统计数据（单独存储，不按地图）
// ---------------------------------------------------------------------------

const DEFAULT_STATS: SessionStats = {
  gamesPlayed: 0,
  totalWins: 0,
  totalLosses: 0,
  totalGoldEarned: 0,
  totalEnemiesKilled: 0,
  avgWaveReached: 0,
};

/**
 * 将已完成的游戏结果记录到聚合 SessionStats 存储中。
 */
export function recordGame(
  result: {
    status: GameState["status"];
    wave: number;
    goldEarned: number;
    enemiesKilled: number;
  },
  storage: StorageAdapter = BrowserStorageAdapter,
): void {
  const stats = readJson<SessionStats>(STATS_KEY, storage) ?? { ...DEFAULT_STATS };
  stats.gamesPlayed += 1;
  if (result.status === "won") {
    stats.totalWins += 1;
  }
  if (result.status === "lost") {
    stats.totalLosses += 1;
  }
  stats.totalGoldEarned += result.goldEarned;
  stats.totalEnemiesKilled += result.enemiesKilled;
  // 滚动平均：newAvg = oldAvg + (newValue - oldAvg) / n
  stats.avgWaveReached =
    stats.avgWaveReached + (result.wave - stats.avgWaveReached) / stats.gamesPlayed;
  writeJson(STATS_KEY, stats, storage);
}

/**
 * 返回当前聚合 SessionStats。
 */
export function getStats(storage: StorageAdapter = BrowserStorageAdapter): SessionStats {
  return readJson<SessionStats>(STATS_KEY, storage) ?? { ...DEFAULT_STATS };
}

/**
 * 将所有聚合统计数据重置为默认值。
 */
export function resetStats(storage: StorageAdapter = BrowserStorageAdapter): void {
  writeJson(STATS_KEY, { ...DEFAULT_STATS }, storage);
}
